<template>
  <div id="trf-toolbar-text-editor" :class="{'is-disabled': disabled}">
    <label :class="{ labelObrigatorio: isTextEditorVazio && isObrigatorio }">
      {{ label }}
    </label>
    <Editor
      @selectionChange="$emit('changeText', content), verificarSeTextEditorEstaVazio()"
      tinymceScriptSrc="/tinymce/tinymce.min.js"
      output-format="html"
      v-model="content"
      :id="id"
      :disabled="disabled"
      :init="{
        selector: 'div',
        resize: false,
        statusbar: false,
        toolbar_mode: 'sliding',
        language: 'pt_BR',
        height: 220,
        menubar: false,
        promotion: false,
        plugins: allowImages ? 'lists image link table code help wordcount' : 'lists link table code help wordcount',
        toolbar: allowImages
          ? 'bold italic strikethrough underline | bullist numlist | outdent  indent | alignleft aligncenter alignright  alignjustify | link unlink image | undo redo fontfamily fontsize '
          : 'bold italic strikethrough underline | bullist numlist | outdent  indent | alignleft aligncenter alignright  alignjustify | link unlink | undo redo fontfamily fontsize ',
        font_family_formats:
          'Serif=serif; Sans=sans; Arial=arial,helvetica,sans-serif; Arial Black=arial black; Courier=courier; Courier New=courier new; Arial Black; Comic Sans MS=comic sans ms; Helvetica=helvetica; Impact=impact; Lucida Grande=lucida grande; Lucida Sans=lucida sans; Tahoma=tahoma; Times=times; Times New Roman=times new roman; Verdana=verdana',
        fontsize_formats: '8pt 10pt 12pt 14pt 16pt 18pt 24pt',
        content_style: 'body { font-size: 12pt; color: #555 }',
        paste_data_images: allowImages,
        images_upload_handler: allowImages ? imageUploadHandler : undefined,
        extended_valid_elements: allowImages ? 'img[width|height|internal-id|src]' : '',
        file_picker_types: allowImages ? 'image' : '',
        file_picker_callback: filePickerCallback,
        setup: setupEditor
     }"
      @init="$emit('ready', false), toolbarTextEditorIsLoaded()"
    />
  </div>
  <span class="tip-label is-size-7">{{ tipLabel }}</span>
  <div class="mensagemDeErro mt-1">
    <span v-if="isTextEditorVazio && isObrigatorio" class="campo-obrigatório">Campo obrigatório.</span>
  </div>
</template>

<script>
import Editor from "@tinymce/tinymce-vue";
import { toastUtils } from '@/util/toast.utils';

const CONTROL_KEYS = [
  'Tab', 'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'
];

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];

export default {
  name: "TrfToolbarTextEditor",
  components: {
    Editor,
  },
  props: {
    tipLabel: String,
    label: String,
    isObrigatorio: Boolean,
    id: String,
    disabled: {
      type: [Boolean],
      default: false
    },
    maxlength: {
      type: Number,
      default: 0
    },
    allowImages: {
      type: Boolean,
      default: false
    },
  },
  emits: ["changeText", "ready"],

  data() {
    return {
      content: "",
      isTextEditorVazio: false,
    };
  },
  methods: {
    iniciarTextEditorComValor(value) {
      return (this.content = value);
    },
    limparCampo() {
      return this.content = ""
    },
    toolbarTextEditorIsLoaded() {
      document.dispatchEvent(
        new CustomEvent('trf-toolbar-text-editor-is-ready', {
          detail: true,
          composed: true
        })
      );
    },
    verificarSeTextEditorEstaVazio() {
      return this.isTextEditorVazio = this.content == "" || this.content == null;
    },
    getValueAsText() {
      return tinymce.activeEditor.getContent({ format: "text" });
    },
    imageUploadHandler(blobInfo) {
      return new Promise((resolve, reject) => {
        const blob = blobInfo.blob();
        
        if (blob.size > MAX_IMAGE_SIZE) {
          toastUtils.alertToast('Imagem muito grande. Tamanho máximo: 5MB.');
          reject('Imagem muito grande');
          return;
        }
        
        const reader = new FileReader();
        reader.onload = function(e) {
          resolve(e.target.result);
        };
        reader.onerror = function() {
          toastUtils.dangerToast('Erro ao processar a imagem.');
          reject('Erro ao ler imagem');
        };
        reader.readAsDataURL(blob);
      });
    },
    filePickerCallback(callback, value, meta) {
      if (!this.allowImages) return;
      if (meta.filetype !== 'image') return;
      
      const input = document.createElement('input');
      input.setAttribute('type', 'file');
      input.setAttribute('accept', 'image/*');
      
      input.onchange = function() {
        const file = this.files[0];
        if (!file) return;
        
        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
          toastUtils.alertToast('Tipo de arquivo não permitido. Use apenas: JPEG, PNG, GIF, WebP ou BMP.');
          return;
        }
        
        if (file.size > MAX_IMAGE_SIZE) {
          toastUtils.alertToast('Imagem muito grande. Tamanho máximo: 5MB.');
          return;
        }
        
        const reader = new FileReader();
        reader.onload = function(e) {
          callback(e.target.result, { alt: file.name });
        };
        reader.onerror = function() {
          toastUtils.dangerToast('Erro ao ler o arquivo. Tente novamente.');
        };
        reader.readAsDataURL(file);
      };
      
      input.click();
    },
    onPaste(e) {
      const clipboardData = e.clipboardData || window.clipboardData;
      if (!clipboardData?.items) return;
      
      for (let i = 0; i < clipboardData.items.length; i++) {
        const item = clipboardData.items[i];
        if (item.type.indexOf('image') === -1) continue;

        if (!this.allowImages) return;

        const file = item.getAsFile();
        if (!file) continue;
        
        if (file.size > MAX_IMAGE_SIZE) {
          toastUtils.alertToast('Imagem muito grande. Tamanho máximo: 5MB.');
          e.preventDefault();
          return;
        }
      }

      if (!this.maxlength) return;

      const textoColado = clipboardData.getData('text/plain').replaceAll('\r', '');
      if (!textoColado) return;
      
      const tamanhoTextoAtual = tinymce.activeEditor.getContent({ format: "text" }).length;
      const tamanhoTextoTotal = tamanhoTextoAtual + textoColado.length;
      
      if (tamanhoTextoTotal <= this.maxlength) return;

      e.preventDefault();
      const sobra = this.maxlength - tamanhoTextoAtual;
      if (sobra === 0) return;

      tinymce.activeEditor.insertContent(textoColado.substring(0, sobra).replaceAll('\n', '<br>'));
    },
    onDrop(e) {
      if (!this.allowImages) return;
      
      const dataTransfer = e.dataTransfer;
      if (!dataTransfer?.files) return;
      
      for (let i = 0; i < dataTransfer.files.length; i++) {
        const file = dataTransfer.files[i];
        if (file.type.indexOf('image') === -1) continue;
        
        if (file.size > MAX_IMAGE_SIZE) {
          e.preventDefault();
          e.stopPropagation();
          toastUtils.alertToast('Imagem muito grande. Tamanho máximo: 5MB.');
          return;
        }
      }
    },
    onKeydown(e) {
      if (!this.maxlength) return;
      if (e.ctrlKey || e.metaKey) return;
      if (CONTROL_KEYS.includes(e.key)) return;

      const length = this.getValueAsText().length;
      if (length < this.maxlength) return;
      
      e.preventDefault();
      e.stopPropagation();
    },
    setupEditor(editor) {
      editor.on('keydown', this.onKeydown);
      editor.on('paste', this.onPaste);
      editor.on('drop', this.onDrop);
    },
  },
};
</script>

<style lang="scss">
#trf-toolbar-text-editor {
  width:100%;
  .tox-tinymce {
    border: 1px solid #ccc;
    border-radius: 4px;
  }

  .tox:not(.tox-tinymce-inline) .tox-editor-header {
    border-bottom: 1px solid #ccc;
  }

  .tox .tox-tbtn {
    border-bottom-left-radius: 3px;
    height: 28px;
    border: 1px solid #ccc;
    width: 30px;
    margin-right: 5px;
  }

  .tox .tox-toolbar__group {
    padding: 0 0px;
  }

  div.tox-toolbar__primary .tox-toolbar__group:first-child  .tox-tbtn:first-child, .tox-toolbar__overflow .tox-toolbar__group:first-child .tox-tbtn:first-child {
    margin-left: 9.5px ;
  }

  .tox .tox-toolbar,
  .tox .tox-toolbar__overflow,
  .tox .tox-toolbar__primary {
    background-image: unset;
  }

  .tox .tox-tbtn--select {
    width: auto;
  }
}
.is-disabled .tox-edit-area {
  cursor: not-allowed!important;
  pointer-events: none!important;
  * {
    cursor: not-allowed!important;
    pointer-events: none!important;
  }
  iframe {
    background: hsl(0deg, 0%, 96%)!important;
    border-color: hsl(0deg, 0%, 96%)!important;
    color: hsl(0deg, 0%, 48%)!important;
  }
}
</style>

<style lang="scss" scoped>
.inValida {
  border: 1px solid #a94442;
}
.mensagemDeErro {
  margin: 0 0 10px 0;
  color: #a94442;
  font-size: 12px;
}
.labelObrigatorio {
  color: #a94442;
}
</style>
